/**
 * The order being put together on the till. Pure functions only: easy to test, and the screen just
 * renders the state. The cart holds *what* was ordered; amounts come from the server's quote.
 */
import type {
  Channel,
  CreateOrderRequest,
  Discount,
  EditOrderRequest,
  LineRequest,
  Order,
  OrderContent,
  ParkedTicket,
} from "../../api/types";

export interface CartLine extends LineRequest {
  /** Stable identity on screen; never sent to the server. */
  key: string;
}

/** The customer on the ticket (prototype S.cust); the internal note is kept with the customer only. */
export interface Customer {
  phone: string;
  name: string;
  /** "E-mail (optioneel, voor bevestiging)" */
  email: string;
  street: string;
  /** "Nr" and "Bus" */
  number: string;
  box: string;
  /** The (deel)gemeente as the shop calls it in Leveringszones. */
  zone: string;
}

export const noCustomer = (): Customer => ({
  phone: "",
  name: "",
  email: "",
  street: "",
  number: "",
  box: "",
  zone: "",
});

/** The open order of an occupied table, loaded on the ticket to order more or to pay. */
export interface TableOrder {
  id: string;
  code: string;
  revision: number;
}

export interface Cart {
  channel: Channel;
  lines: CartLine[];
  selected: string | null;
  customer: Customer;
  /** The table as it is named on the floor plan; "" when none was chosen yet. */
  table: string;
  /** Set when the chosen table was occupied: saving adds to that order instead of a new one. */
  tableOrder: TableOrder | null;
  note: string;
  discount: Discount | null;
  /** Spaarkaart: the customer uses a full card at checkout. */
  redeemPoints: boolean;
}

export const emptyCart = (channel: Channel = "takeaway"): Cart => ({
  channel,
  lines: [],
  selected: null,
  customer: noCustomer(),
  table: "",
  tableOrder: null,
  note: "",
  discount: null,
  redeemPoints: false,
});

export type CartAction =
  | { type: "add"; line: LineRequest; key: string }
  | { type: "replace"; key: string; line: LineRequest }
  | { type: "changeQuantity"; key: string; delta: number }
  | { type: "toggleFree"; key: string }
  | { type: "duplicate"; key: string; newKey: string }
  | { type: "remove"; key: string }
  | { type: "select"; key: string | null }
  | { type: "channel"; channel: Channel }
  | { type: "customer"; field: keyof Customer; value: string }
  /** A known customer recognised by the phone number, or a call taken over: the whole customer at once. */
  | { type: "fillCustomer"; customer: Customer }
  /** Fills in only what is still empty (a caller known from Takeaway.com or Mymenu.be); the address on a delivery. */
  | { type: "fillEmpty"; customer: Pick<Customer, "name" | "street" | "number" | "zone"> }
  /** A table chosen on the floor plan; `order` is its open order when the table is occupied. */
  | { type: "table"; table: string; order: Order | null }
  /** The guests moved to another table (the order came back from the server). */
  | { type: "tableMoved"; order: Order }
  | { type: "note"; value: string }
  | { type: "discount"; discount: Discount | null }
  | { type: "redeem"; on: boolean }
  | { type: "clear" };

const MAX_QUANTITY = 999;

export function cartReducer(cart: Cart, action: CartAction): Cart {
  switch (action.type) {
    case "add": {
      // the free product of the action (Acties) is there once, quantity 1
      if (action.line.promotion === true && cart.lines.some((line) => line.promotion === true)) return cart;
      // the same product with the same options adds up on one line, which is then the selected one
      const same = cart.lines.find((line) => sameContent(line, action.line));
      if (same) {
        return {
          ...withLine(cart, same.key, (line) => ({
            ...line,
            quantity: Math.min(MAX_QUANTITY, line.quantity + action.line.quantity),
          })),
          selected: same.key,
        };
      }
      return { ...cart, lines: [...cart.lines, { ...action.line, key: action.key }], selected: action.key };
    }
    case "replace":
      return withLine(cart, action.key, () => ({ ...action.line, key: action.key }));
    case "changeQuantity": {
      const line = cart.lines.find((candidate) => candidate.key === action.key);
      if (!line) return cart;
      if (line.promotion === true && action.delta > 0) return cart;
      const quantity = Math.min(MAX_QUANTITY, line.quantity + action.delta);
      if (quantity < 1) return removeLine(cart, action.key);
      return withLine(cart, action.key, (current) => ({ ...current, quantity }));
    }
    case "toggleFree":
      return withLine(cart, action.key, (line) =>
        line.promotion === true ? line : withFree(line, line.free !== true),
      );
    case "duplicate": {
      const index = cart.lines.findIndex((line) => line.key === action.key);
      if (index < 0 || cart.lines[index]!.promotion === true) return cart;
      const copy = { ...cart.lines[index]!, key: action.newKey };
      const lines = [...cart.lines.slice(0, index + 1), copy, ...cart.lines.slice(index + 1)];
      return { ...cart, lines, selected: action.newKey };
    }
    case "remove":
      return removeLine(cart, action.key);
    case "select":
      return { ...cart, selected: action.key };
    case "channel":
      if (action.channel === cart.channel) return cart;
      // the lines of an open table belong to that table: leaving it starts a new ticket
      if (cart.tableOrder) return emptyCart(action.channel);
      return { ...cart, channel: action.channel, table: "", redeemPoints: false };
    case "customer":
      return { ...cart, customer: { ...cart.customer, [action.field]: action.value } };
    case "fillCustomer":
      return { ...cart, customer: action.customer };
    case "fillEmpty": {
      const fields =
        cart.channel === "delivery" ? (["name", "zone", "street", "number"] as const) : (["name"] as const);
      const customer = { ...cart.customer };
      for (const field of fields)
        if (!customer[field] && action.customer[field]) customer[field] = action.customer[field];
      return { ...cart, customer };
    }
    case "table":
      return chooseTable(cart, action.table, action.order);
    case "tableMoved":
      return cart.tableOrder?.id === action.order.id
        ? { ...cart, table: action.order.table ?? cart.table, tableOrder: tableOrderOf(action.order) }
        : cart;
    case "note":
      return { ...cart, note: action.value };
    case "discount":
      return { ...cart, discount: action.discount };
    case "redeem":
      return { ...cart, redeemPoints: action.on };
    case "clear":
      return emptyCart(cart.channel);
  }
}

/**
 * A free table keeps what is already on the ticket. An occupied table brings its open order back on
 * the ticket (as in the prototype), followed by anything that was typed before choosing it.
 */
function chooseTable(cart: Cart, table: string, order: Order | null): Cart {
  const typed = cart.tableOrder ? [] : cart.lines;
  if (!order) return { ...cart, channel: "table", table, tableOrder: null, lines: typed, selected: null };
  const loaded = cartFromOrder(order);
  return {
    ...cart,
    channel: "table",
    table,
    tableOrder: tableOrderOf(order),
    lines: [...loaded.lines, ...typed],
    selected: null,
    note: loaded.note,
    discount: loaded.discount,
  };
}

function tableOrderOf(order: Order): TableOrder {
  return { id: order.id, code: order.code, revision: order.revision };
}

function withLine(cart: Cart, key: string, update: (line: CartLine) => CartLine): Cart {
  return { ...cart, lines: cart.lines.map((line) => (line.key === key ? update(line) : line)) };
}

function withFree(line: CartLine, free: boolean): CartLine {
  const { free: _previous, ...rest } = line;
  return free ? { ...rest, free: true } : rest;
}

function removeLine(cart: Cart, key: string): Cart {
  const index = cart.lines.findIndex((line) => line.key === key);
  const lines = cart.lines.filter((line) => line.key !== key);
  // keep a line selected, so −1 −1 −1 works through the ticket
  const next = lines[Math.min(index, lines.length - 1)]?.key ?? null;
  return { ...cart, lines, selected: cart.selected === key ? next : cart.selected };
}

/** The line as the server receives it: without the screen key, and without empty options. */
export function toLineRequest(line: LineRequest): LineRequest {
  const request: LineRequest = { productId: line.productId, quantity: line.quantity };
  if (line.variant !== undefined) request.variant = line.variant;
  const choices = Object.fromEntries(Object.entries(line.choices ?? {}).filter(([, picked]) => picked.length > 0));
  if (Object.keys(choices).length > 0) request.choices = choices;
  if (line.without?.length) request.without = line.without;
  if (line.only?.length) request.only = line.only;
  const note = line.note?.trim();
  if (note) request.note = note;
  if (line.free === true) request.free = true;
  if (line.promotion === true) request.promotion = true;
  return request;
}

/** A stable text per line content, to know whether a quote still matches the ticket. */
export function fingerprint(line: LineRequest): string {
  return JSON.stringify(toLineRequest(line));
}

function sameContent(a: LineRequest, b: LineRequest): boolean {
  return fingerprint({ ...a, quantity: 1 }) === fingerprint({ ...b, quantity: 1 });
}

export function toContent(cart: Cart): OrderContent | null {
  if (cart.lines.length === 0) return null;
  const content: OrderContent = { channel: cart.channel, lines: cart.lines.map(toLineRequest) };
  if (cart.discount) content.discount = cart.discount;
  // a delivery: the zone and the street's distance decide the fee (Leveringszones); a full loyalty card
  // needs the phone
  const customer: Record<string, string> = {};
  const zone = cart.customer.zone.trim();
  const street = cart.customer.street.trim();
  if (cart.channel === "delivery" && zone) customer.zone = zone;
  if (cart.channel === "delivery" && street) customer.street = street;
  if (cart.redeemPoints && cart.channel !== "table") {
    content.redeemPoints = true;
    customer.phone = cart.customer.phone.trim();
  }
  if (Object.keys(customer).length > 0) content.customer = customer;
  return content;
}

export type Missing = "empty" | "table" | "zone" | "street";

/** What is still missing before the order can be saved; null when complete (prototype canPay). */
export function missingDetails(cart: Cart): Missing | null {
  if (cart.lines.length === 0) return "empty";
  if (cart.channel === "table" && cart.table.trim() === "") return "table";
  if (cart.channel === "delivery" && cart.customer.zone.trim() === "") return "zone";
  if (cart.channel === "delivery" && cart.customer.street.trim() === "") return "street";
  return null;
}

export function toCreateRequest(cart: Cart): CreateOrderRequest {
  const content = toContent(cart);
  if (content === null) throw new Error("an order needs at least one line");
  const request: CreateOrderRequest = { ...content };
  if (cart.channel === "table") request.table = cart.table.trim();
  const customer = Object.fromEntries(
    (Object.entries(cart.customer) as [keyof Customer, string][])
      .map(([field, value]) => [field, value.trim()] as const)
      .filter(([, value]) => value !== ""),
  );
  if (cart.channel !== "table" && Object.keys(customer).length > 0) request.customer = customer;
  const note = cart.note.trim();
  if (note) request.note = note;
  return request;
}

/** The customer fields of an order or a parked ticket. */
function customerOf(customer: Record<string, string>): Customer {
  return {
    phone: customer.phone ?? "",
    name: customer.name ?? "",
    email: customer.email ?? "",
    street: customer.street ?? "",
    number: customer.number ?? "",
    box: customer.box ?? "",
    zone: customer.zone ?? "",
  };
}

/** An existing order back on the ticket, to edit it. Lines keep what was originally ordered. */
export function cartFromOrder(order: Order): Cart {
  return {
    channel: order.channel,
    lines: order.lines.flatMap((line, index) =>
      line.request ? [{ ...line.request, key: `edit-${String(index)}` }] : [],
    ),
    selected: null,
    customer: customerOf(order.customer),
    table: order.table ?? "",
    tableOrder: null,
    note: order.note ?? "",
    discount: order.discount,
    redeemPoints: false,
  };
}

/** A parked ticket back on the ticket ("Verder werken", prototype renderParked). */
export function cartFromTicket(ticket: ParkedTicket): Cart {
  return {
    ...emptyCart(ticket.channel),
    lines: ticket.lines.map((line, index) => ({ ...line, key: `parked-${String(index)}` })),
    customer: customerOf(ticket.customer ?? {}),
    note: ticket.note ?? "",
  };
}

/** What Parkeren keeps: the lines, the customer and the note, as for a new order. */
export function toParkRequest(cart: Cart): ParkedTicket {
  if (cart.channel === "table") throw new Error("a table is never parked");
  const { lines, customer, note } = toCreateRequest(cart);
  return { channel: cart.channel, lines, ...(customer && { customer }), ...(note !== undefined && { note }) };
}

/** The edit as the server receives it; `revision` refuses it when another device changed the order meanwhile. */
export function toEditRequest(cart: Cart, revision: number): EditOrderRequest {
  const { lines, customer, note } = toCreateRequest(cart);
  return { expectedRevision: revision, lines, customer: customer ?? {}, note: note ?? "" };
}

/**
 * "Korting" while editing (Bewerken): true when the ticket has another discount than the order, or none
 * where the order has one. Only then the edit sends it; otherwise the order keeps what it has.
 */
export function discountChanged(cart: Cart, order: Order): boolean {
  return JSON.stringify(cart.discount) !== JSON.stringify(order.discount);
}

/** True when the ticket differs from what the order holds (lines or note): then it must be saved first. */
export function differsFrom(cart: Cart, order: Order): boolean {
  const saved = cartFromOrder(order);
  const text = (lines: CartLine[]) => lines.map(fingerprint).join("|");
  return text(saved.lines) !== text(cart.lines) || saved.note.trim() !== cart.note.trim();
}

/**
 * An edit changes something: the lines, the note, the customer or the discount (prototype saveEdit "Niets
 * gewijzigd").
 */
export function editChanges(cart: Cart, order: Order): boolean {
  const saved = cartFromOrder(order).customer;
  const changedCustomer =
    cart.channel !== "table" &&
    (Object.keys(saved) as (keyof Customer)[]).some((field) => saved[field].trim() !== cart.customer[field].trim());
  return differsFrom(cart, order) || changedCustomer || discountChanged(cart, order);
}
